import { useEffect, useMemo, useState } from 'react'

import { encode, decode } from 'js-base64'
import { Link } from 'react-router-dom'

import { Tag } from '@tribeplatform/gql-client/types'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { TagFormModal } from '@tribeplatform/react-components/Tag'
import { useRouter } from '@tribeplatform/react-sdk'
import { useDeleteTag, useSpaceTags } from '@tribeplatform/react-sdk/hooks'
import { IconButton, Button } from '@tribeplatform/react-ui-kit/Button'
import { confirm } from '@tribeplatform/react-ui-kit/Dialog'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { useQuery } from '../../../hooks/useQuery.js'
import { useDebounce } from '../../../utils/useDebounce.js'
import { Table } from '../../Table/index.js'

export const Tags = () => {
  const { $t } = useI18n()
  const [tagModal, setTagModal] = useState<Tag | boolean>()

  let initialState = {
    orderBy: {
      column: undefined,
      reverse: true,
    },
    query: null,
    filterBy: [],
  }

  const { replace } = useRouter()
  const { state: queryState } = useQuery()
  if (queryState) {
    try {
      initialState = { ...initialState, ...JSON.parse(decode(queryState)) }
    } catch (error) {
      logger.error('Could not parse initial state for members')
    }
  }

  const [state, setState] = useState(initialState)

  const debouncedQuery = useDebounce(state.query, 300)

  useEffect(() => {
    replace(`?state=${encode(JSON.stringify(state))}`)
  }, [state])

  const { mutateAsync: deleteTag } = useDeleteTag()
  const {
    data,
    isInitialLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useSpaceTags({
    fields: 'all',
    variables: {
      limit: 15,
      query: debouncedQuery,
    },
    useInfiniteQueryOptions: { keepPreviousData: true },
  })

  const { data: totalTagsData } = useSpaceTags({
    fields: 'basic',
    variables: {
      limit: 0,
    },
  })

  const totalTags = totalTagsData?.pages?.[0]?.totalCount

  const onDelete = async tag => {
    if (!tag?.id) {
      return
    }

    const confirmed = await confirm({
      title: $t({
        defaultMessage: 'Are you sure you want to remove this {TAG}?',
        id: 'Admin.Tags.Remove.Confirmation',
      }),
      description: $t({
        defaultMessage:
          'Removing a {TAG} will also remove it from all posts and is irreversible.',
        id: 'Admin.Tags.Remove.Confirmation.Description',
      }),
      proceedLabel: $t({ defaultMessage: 'Remove', id: 'Generics.Remove' }),
      cancelLabel: $t({ defaultMessage: 'Cancel', id: 'Generics.Cancel' }),
      danger: true,
    })
    if (!confirmed) {
      return
    }

    try {
      await deleteTag({ id: tag?.id })
      toast({
        title: $t({
          defaultMessage: '{TAG_CC} removed',
          id: 'Admin.Tag.Removed',
        }),
        description: $t({
          defaultMessage: '{TAG_CC} is successfully removed.',
          id: 'Admin.Tag.Removed.Description',
        }),
        status: 'success',
      })
    } catch (err) {
      toast({
        title: $t({
          defaultMessage: 'Something went wrong',
          id: 'Generics.SomethingWentWrong',
        }),
        description: $t({
          defaultMessage: 'Cannot remove this {TAG}.',
          id: 'Admin.Tag.FailedToRemove',
        }),
        status: 'error',
      })
    }
  }

  const columns = useMemo(
    () => [
      {
        Header: $t({ defaultMessage: 'ID', id: 'Generics.ID' }),
        accessor: 'id',
        renderer: 'primary',
        visible: false,
        width: 'lg',
      },
      {
        Header: $t({ defaultMessage: 'Title', id: 'Generics.Title' }),
        accessor: 'title',
        renderer: 'tagPrimary',
        visible: true,
        width: 'lg',
      },
      {
        Header: $t({ defaultMessage: 'Slug', id: 'Generics.Slug' }),
        accessor: 'slug',
        visible: true,
        width: 'md',
      },
      {
        Header: $t({
          defaultMessage: 'Description',
          id: 'Generics.Description',
        }),
        accessor: 'description',
        visible: true,
        width: 'lg',
      },
      {
        visible: true,
        accessor: 'actions',
        locked: true,
        width: 'collapse',
        renderer: (_, tag) => (
          <Dropdown>
            <Dropdown.ButtonMinimal as="div">
              <IconButton
                variant="tertiaryNeutral"
                rounded
                icon={<SvgIcon name="dots-horizontal" />}
                size="sm"
              />
            </Dropdown.ButtonMinimal>
            <Dropdown.Items>
              <Dropdown.Item
                as={Link}
                to={`/search?type=post&query=${encodeURIComponent(tag.title)}`}
                leadingIcon={<SvgIcon name="tag" />}
              >
                <T defaultMessage="View {TAG}" id="Admin.Tag.View" />
              </Dropdown.Item>
              <Dropdown.Item
                leadingIcon={<SvgIcon name="edit" />}
                onClick={() => {
                  setTagModal(tag)
                }}
              >
                <T defaultMessage="Edit {TAG}" id="Admin.Tag.Edit" />
              </Dropdown.Item>
              <Dropdown.Item
                leadingIcon={<SvgIcon name="trash" />}
                onClick={() => {
                  onDelete(tag)
                }}
              >
                <T defaultMessage="Remove {TAG}" id="Admin.Tag.Remove" />
              </Dropdown.Item>
            </Dropdown.Items>
          </Dropdown>
        ),
      },
    ],
    [],
  )

  return (
    <div className="w-full">
      <Table
        isLastColumnSticky
        storageId="tags"
        isLoading={isInitialLoading}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        columns={columns}
        data={data}
        title={$t({ defaultMessage: '{TAGS_CC}', id: 'Generics.Tags' })}
        entityType="tag"
        customTotalCount={totalTags}
        state={state}
        onStateChange={setState}
        keyboardControl
        actions={
          <Button
            size="lg"
            variant="primary"
            onClick={() => setTagModal({} as Tag)}
          >
            <T defaultMessage="Add {TAG}" id="Admin.Tags.Add" />
          </Button>
        }
      />
      {tagModal ? (
        <TagFormModal
          open
          onClose={() => setTagModal(false)}
          tag={tagModal as Tag}
        />
      ) : null}
    </div>
  )
}
